/**
 * The PipelineMap: the left region of the "Write" studio. A calm, top-to-bottom
 * "subway" view of how the issue is built, NOT a crossing-wire graph (drawn edges
 * are a later Wire mode). Three lanes read down the column:
 *
 *   SOURCES   the distinct sources feeding the newsletter, each a colour-coded node
 *      ↓      with a plain volume readout; a legend maps colour -> source.
 *   SECTIONS  the issue's sections in reading order, each showing its Treatment chip
 *      ↓      (the honest "how much AI" knob) and colour binding dots for the sources
 *             that feed it, so "fed by three sources" reads at a glance with no wires.
 *   ISSUE     a single node labelled "Issue" (no number: all issues are automated).
 *
 * Selecting any node lifts the selection to the studio, which binds the inspector and
 * cross-highlights the matching rendered block in the centre issue (a section node's
 * id IS its content block id, so the highlight is free).
 */
import type { PipelineSectionNode, PipelineSource, SelectedNode } from './pipeline';
import { sourceChannelLabel, treatmentLabel } from './pipeline';

/** A short glyph per section role, echoing the mockup's node icons. Decorative only. */
const ROLE_ICON: Record<PipelineSectionNode['role'], string> = {
  intro: '✳',
  story: '★',
  commentary: '✍',
  digest: '≣',
  links: '↗',
  signoff: '~',
};

/** A glyph tile per source shape, so source nodes read as first-class as section nodes. */
function sourceIcon(source: PipelineSource): string {
  return source.ref.sourceType === 'site-feed' ? '▤' : '⌕';
}

/** The plain role phrase shown in a section node's sub-line (mockup: "spin lead story"). */
const ROLE_PHRASE: Record<PipelineSectionNode['role'], string> = {
  intro: 'opening',
  story: 'our take',
  commentary: 'full commentary',
  digest: 'digest',
  links: 'link roundup',
  signoff: 'sign-off',
};

function isSelected(selected: SelectedNode | null, kind: SelectedNode['kind'], id: string): boolean {
  return selected !== null && selected.kind === kind && selected.id === id;
}

export function PipelineMap({
  sources,
  sectionNodes,
  issueStatus,
  selected,
  highlightId = null,
  onSelect,
}: {
  readonly sources: readonly PipelineSource[];
  readonly sectionNodes: readonly PipelineSectionNode[];
  /** The issue's lifecycle word for the Issue node readout (draft / ready / …). */
  readonly issueStatus: string | null;
  readonly selected: SelectedNode | null;
  /** The block id currently highlighted (e.g. hovering its reader block lights its node). */
  readonly highlightId?: string | null;
  readonly onSelect: (node: SelectedNode) => void;
}): React.JSX.Element {
  const keptCount = sectionNodes.filter((node) => node.status !== 'dismissed').length;

  return (
    <section className="nlv__panel nlv__map" aria-labelledby="nlv-map-heading">
      <div className="nlv__map-head">
        <h2 id="nlv-map-heading" className="nlv__panel-title">
          How this issue is built
        </h2>
      </div>

      {sources.length > 0 && (
        <ul className="nlv__map-legend" aria-label="Source colours">
          {sources.map((source) => (
            <li key={source.key} className="nlv__map-legend-item" title={source.name}>
              <span
                className="nlv__map-dot"
                style={{ background: source.color }}
                aria-hidden="true"
              />
              {source.shortName}
            </li>
          ))}
        </ul>
      )}

      <div className="nlv__map-body">
        {/* SOURCES lane */}
        <p className="nlv__map-lane-label">
          {sources.length > 0
            ? `Sources · ${String(sources.length)} feeding`
            : 'Sources'}
        </p>
        {sources.length === 0 ? (
          <p className="nlv__map-empty">
            As you keep sources on the left, they appear here, colour-coded.
          </p>
        ) : (
          sources.map((source) => (
            <button
              key={source.key}
              type="button"
              className={`nlv__map-node nlv__map-node--source${
                isSelected(selected, 'source', source.key) ? ' is-selected' : ''
              }`}
              style={{ '--src-color': source.color } as React.CSSProperties}
              aria-pressed={isSelected(selected, 'source', source.key)}
              onClick={() => onSelect({ kind: 'source', id: source.key })}
            >
              <span className="nlv__map-node-ico" aria-hidden="true">
                {sourceIcon(source)}
              </span>
              <span className="nlv__map-node-body">
                <span className="nlv__map-node-name">{source.name}</span>
                <span className="nlv__map-node-sub">{sourceChannelLabel(source.ref)}</span>
              </span>
              <span
                className="nlv__map-count"
                aria-label={`${String(source.ref.metrics.volume)} items`}
              >
                {source.ref.metrics.volume}
              </span>
            </button>
          ))
        )}

        <div className="nlv__map-flow" aria-hidden="true">
          <span className="nlv__map-flow-line" />
          <span className="nlv__map-flow-chev">▾</span>
        </div>

        {/* SECTIONS lane */}
        <p className="nlv__map-lane-label">Sections · in reading order</p>
        {sectionNodes.length === 0 ? (
          <p className="nlv__map-empty">Your sections appear here once the issue is drafted.</p>
        ) : (
          sectionNodes.map((node) => {
            const fedBy =
              node.boundSources.length > 0
                ? `Fed by ${node.boundSources.map((source) => source.name).join(', ')}`
                : undefined;
            return (
              <button
                key={node.blockId}
                type="button"
                className={`nlv__map-node nlv__map-node--section${
                  node.status === 'dismissed' ? ' is-dismissed' : ''
                }${isSelected(selected, 'section', node.blockId) ? ' is-selected' : ''}${
                  highlightId === node.blockId ? ' is-active' : ''
                }`}
                aria-pressed={isSelected(selected, 'section', node.blockId)}
                onClick={() => onSelect({ kind: 'section', id: node.blockId })}
              >
                <span className="nlv__map-node-ico" aria-hidden="true">
                  {ROLE_ICON[node.role]}
                </span>
                <span className="nlv__map-node-body">
                  <span className="nlv__map-node-name">{node.title}</span>
                  <span className="nlv__map-node-sub">
                    {node.treatment !== null && (
                      <span
                        className="nlv__treatment nlv__treatment--ai"
                        title="How our AI writes this section. Change it in the inspector."
                      >
                        {treatmentLabel(node.treatment)}
                      </span>
                    )}
                    {node.status === 'dismissed' ? 'Set aside' : ROLE_PHRASE[node.role]}
                  </span>
                </span>
                {fedBy !== undefined && (
                  <span className="nlv__map-binddots" title={fedBy} aria-label={fedBy}>
                    {node.boundSources.map((source, index) => (
                      <span
                        key={index}
                        className="nlv__map-binddot"
                        style={{ background: source.color }}
                      />
                    ))}
                  </span>
                )}
              </button>
            );
          })
        )}

        <div className="nlv__map-flow" aria-hidden="true">
          <span className="nlv__map-flow-line" />
          <span className="nlv__map-flow-chev">▾</span>
        </div>

        {/* ISSUE node */}
        <button
          type="button"
          className={`nlv__map-node nlv__map-node--issue${
            isSelected(selected, 'issue', 'issue') ? ' is-selected' : ''
          }`}
          aria-pressed={isSelected(selected, 'issue', 'issue')}
          onClick={() => onSelect({ kind: 'issue', id: 'issue' })}
        >
          <span className="nlv__map-node-ico nlv__map-node-ico--issue" aria-hidden="true">
            ✉
          </span>
          <span className="nlv__map-node-body">
            <span className="nlv__map-node-name">Issue</span>
            <span className="nlv__map-node-sub">
              {`${String(keptCount)} section${keptCount === 1 ? '' : 's'} · ${
                issueStatus ?? 'draft'
              }`}
            </span>
          </span>
        </button>
      </div>
    </section>
  );
}
